import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const PUBLIC_API = `${BACKEND_URL}/api`;

/** Resolve URL de imagem: absoluta -> mantém; relativa /api/... -> prefixa backend. */
export function resolveImage(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

/** Cache simples em módulo para evitar refetch em cada mount. */
const cache = { services: null, solutions: null, news: null, "group-companies": null, certifications: null, offices: null, promises: {} };

async function fetchList(kind) {
  if (cache[kind]) return cache[kind];
  if (cache.promises[kind]) return cache.promises[kind];
  cache.promises[kind] = axios
    .get(`${PUBLIC_API}/${kind}`)
    .then((r) => { cache[kind] = r.data; return r.data; })
    .finally(() => { delete cache.promises[kind]; });
  return cache.promises[kind];
}

export function invalidateCms() {
  cache.services = null;
  cache.solutions = null;
  cache.news = null;
  cache["group-companies"] = null;
  cache.certifications = null;
  cache.offices = null;
  cache.solutionsLayout = null;
  invalidatePageTexts();
}

/** Layout do mosaico bento de Soluções (12 posições configuráveis pelo admin). */
export function useSolutionsLayout() {
  const [data, setData] = useState(cache.solutionsLayout || null);
  const [loading, setLoading] = useState(!cache.solutionsLayout);
  useEffect(() => {
    let alive = true;
    if (cache.solutionsLayout) { setLoading(false); return; }
    if (cache.solutionsLayoutPromise) {
      cache.solutionsLayoutPromise.then((d) => alive && (setData(d), setLoading(false)));
      return () => { alive = false; };
    }
    cache.solutionsLayoutPromise = axios
      .get(`${PUBLIC_API}/solutions-layout`)
      .then((r) => { cache.solutionsLayout = r.data; return r.data; })
      .catch(() => ({ cells: [], updated_at: null }))
      .finally(() => { delete cache.solutionsLayoutPromise; });
    cache.solutionsLayoutPromise.then((d) => alive && (setData(d), setLoading(false)));
    return () => { alive = false; };
  }, []);
  return { data: data || { cells: [], updated_at: null }, loading };
}

export function invalidateSolutionsLayout() {
  cache.solutionsLayout = null;
  delete cache.solutionsLayoutPromise;
}


export function useServices() {
  const [data, setData] = useState(cache.services || []);
  const [loading, setLoading] = useState(!cache.services);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchList("services")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  return { data, loading, error };
}

export function useSolutions() {
  const [data, setData] = useState(cache.solutions || []);
  const [loading, setLoading] = useState(!cache.solutions);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchList("solutions")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  return { data, loading, error };
}

export function useService(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${PUBLIC_API}/services/${slug}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);
  return { data, loading, error };
}

export function useSolution(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${PUBLIC_API}/solutions/${slug}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);
  return { data, loading, error };
}

export function useNews() {
  const [data, setData] = useState(cache.news || []);
  const [loading, setLoading] = useState(!cache.news);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchList("news")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  return { data, loading, error };
}

export function useNewsItem(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${PUBLIC_API}/news/${slug}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);
  return { data, loading, error };
}


/** Site-wide settings (autoscroll de notícias, etc.) — cache global. */
// Persistência em localStorage para eliminar "flash of stale content" entre visitas.
// TTL curto (7 dias) e background revalidate mantêm os dados frescos.
const SITE_SETTINGS_STORAGE_KEY = "inxpek_site_settings_cache_v1";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

function _readSiteSettingsFromStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (!obj.t || Date.now() - obj.t > CACHE_TTL_MS) return null;
    return obj.data || null;
  } catch {
    return null;
  }
}
function _writeSiteSettingsToStorage(data) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(
      SITE_SETTINGS_STORAGE_KEY,
      JSON.stringify({ t: Date.now(), data })
    );
  } catch { /* quota / privacy mode */ }
}

let siteSettingsCache = _readSiteSettingsFromStorage();
let siteSettingsPromise = null;

export async function fetchSiteSettings() {
  if (siteSettingsCache) return siteSettingsCache;
  if (siteSettingsPromise) return siteSettingsPromise;
  siteSettingsPromise = axios
    .get(`${PUBLIC_API}/site-settings`)
    .then((r) => {
      siteSettingsCache = r.data;
      _writeSiteSettingsToStorage(r.data);
      return r.data;
    })
    .catch(() => {
      const fallback = { news_autoscroll_ms: 3000, news_autoscroll_enabled: true };
      siteSettingsCache = fallback;
      return fallback;
    })
    .finally(() => {
      siteSettingsPromise = null;
    });
  return siteSettingsPromise;
}

export function useSiteSettings() {
  const [data, setData] = useState(siteSettingsCache || { news_autoscroll_ms: 3000, news_autoscroll_enabled: true, logo_light_url: "", logo_dark_url: "" });
  useEffect(() => {
    let alive = true;
    // Revalida sempre em background: garante que se algo mudou no CMS,
    // a UI actualiza discretamente (sem flash de conteúdo antigo).
    axios
      .get(`${PUBLIC_API}/site-settings`)
      .then((r) => {
        if (!alive) return;
        siteSettingsCache = r.data;
        _writeSiteSettingsToStorage(r.data);
        setData(r.data);
      })
      .catch(() => { /* silent — mantém o cache existente */ });
    return () => { alive = false; };
  }, []);
  return data;
}

/** Resolve URLs do branding com fallback para ficheiros locais. */
export function useBranding() {
  const settings = useSiteSettings();
  return {
    logoLight: settings.logo_light_url ? resolveImage(settings.logo_light_url) : "/logo-light.png",
    logoDark: settings.logo_dark_url ? resolveImage(settings.logo_dark_url) : "/logo-dark.png",
    heroVideo: settings.hero_video_url ? resolveImage(settings.hero_video_url) : "/videos/hero.mp4",
    heroPoster: settings.hero_poster_url ? resolveImage(settings.hero_poster_url) : "/videos/hero-poster.jpg",
    heroMobileFallbackEnabled: settings.hero_mobile_fallback_enabled !== false,
    heroVideoStartDelay: Number.isFinite(Number(settings.hero_video_start_delay))
      ? Number(settings.hero_video_start_delay)
      : 3,
    heroTitle: settings.hero_title || "Conformidade Total",
    heroSubtitle: settings.hero_subtitle || "Inteligência. Tecnologia. Confiança.",
    heroDescription:
      settings.hero_description ||
      "Ajudamos empresas e particulares a garantir a conformidade dos seus ativos, proporcionando segurança e tranquilidade através de conhecimento especializado.",
  };
}

/** Cache em memória + localStorage para page-texts — evita flash de fallback ao navegar
 *  e ao recarregar a página. TTL de 7 dias com revalidação em background. */
const PAGE_TEXTS_STORAGE_KEY = "inxpek_page_texts_cache_v1";
// TTL curto (60s) para page-texts — permite que edições no admin apareçam rapidamente no site público.
const PAGE_TEXTS_TTL_MS = 60 * 1000;

function _readPageTextsFromStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const raw = window.localStorage.getItem(PAGE_TEXTS_STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return {};
    const now = Date.now();
    const out = {};
    for (const [k, entry] of Object.entries(obj)) {
      if (!entry || typeof entry !== "object") continue;
      if (!entry.t || now - entry.t > PAGE_TEXTS_TTL_MS) continue;
      out[k] = entry.data;
    }
    return out;
  } catch {
    return {};
  }
}
function _writePageTextToStorage(pageId, data) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const raw = window.localStorage.getItem(PAGE_TEXTS_STORAGE_KEY);
    let obj = {};
    if (raw) {
      try { obj = JSON.parse(raw) || {}; } catch { obj = {}; }
    }
    obj[pageId] = { t: Date.now(), data };
    window.localStorage.setItem(PAGE_TEXTS_STORAGE_KEY, JSON.stringify(obj));
  } catch { /* quota / privacy mode */ }
}
function _clearPageTextStorage(pageId) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (!pageId) { window.localStorage.removeItem(PAGE_TEXTS_STORAGE_KEY); return; }
    const raw = window.localStorage.getItem(PAGE_TEXTS_STORAGE_KEY);
    if (!raw) return;
    let obj = {};
    try { obj = JSON.parse(raw) || {}; } catch { obj = {}; }
    delete obj[pageId];
    window.localStorage.setItem(PAGE_TEXTS_STORAGE_KEY, JSON.stringify(obj));
  } catch { /* ignore */ }
}

const pageTextsCache = _readPageTextsFromStorage();    // { pageId: data }
const pageTextsPromises = {}; // { pageId: promise }

export function invalidatePageTexts(pageId) {
  if (pageId) { delete pageTextsCache[pageId]; _clearPageTextStorage(pageId); }
  else { Object.keys(pageTextsCache).forEach((k) => delete pageTextsCache[k]); _clearPageTextStorage(); }
}

/** Pré-carrega uma lista de page-texts (sem bloquear) para evitar flash de fallback ao navegar. */
export function preloadPageTexts(ids = []) {
  ids.forEach((pageId) => {
    if (!pageId || pageTextsPromises[pageId]) return;
    pageTextsPromises[pageId] = axios
      .get(`${PUBLIC_API}/page-texts/${pageId}`)
      .then((r) => { pageTextsCache[pageId] = r.data; _writePageTextToStorage(pageId, r.data); return r.data; })
      .catch(() => null)
      .finally(() => { delete pageTextsPromises[pageId]; });
  });
}

/** Hook para textos editáveis de uma página. Faz cache em memória + localStorage (evita flash). */
export function usePageText(pageId, fallback = { eyebrow: "", title: "", intro_paragraphs: [] }) {
  const cached = pageTextsCache[pageId];
  const [data, setData] = useState(cached || fallback);
  const [loading, setLoading] = useState(!cached);
  useEffect(() => {
    let alive = true;
    // Se já temos em cache (memória ou localStorage), sincroniza imediatamente.
    // Depois revalida SEMPRE em background para pegar mudanças recentes.
    if (pageTextsCache[pageId]) {
      setData(pageTextsCache[pageId]);
      setLoading(false);
    }
    // Dedupe de pedidos concorrentes para o mesmo pageId.
    if (!pageTextsPromises[pageId]) {
      pageTextsPromises[pageId] = axios
        .get(`${PUBLIC_API}/page-texts/${pageId}`)
        .then((r) => { pageTextsCache[pageId] = r.data; _writePageTextToStorage(pageId, r.data); return r.data; })
        .finally(() => { delete pageTextsPromises[pageId]; });
    }
    pageTextsPromises[pageId]
      .then((d) => { if (alive && d) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pageId]);
  return { data, loading };
}

export function invalidateSiteSettings() {
  siteSettingsCache = null;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(SITE_SETTINGS_STORAGE_KEY);
    }
  } catch { /* ignore */ }
}


/**
 * Deteta se estamos numa condição em que devemos servir só a imagem de fallback (sem carregar vídeo):
 *  - Ligação lenta (`saveData=true`, ou effectiveType 2g/slow-2g)
 *  - Utilizador com `prefers-reduced-data` ativo
 *
 * Nota: viewport mobile já NÃO é motivo suficiente por si só. Telemóveis modernos em 4G/5G/Wi-Fi
 * conseguem reproduzir o vídeo sem problemas — só bloqueamos quando o próprio browser ou o utilizador
 * sinaliza explicitamente que a ligação é lenta ou que quer poupar dados.
 *
 *  Retorna null enquanto se avalia (para permitir SSR-safe rendering) e depois true/false.
 */
export function useHeroFallbackMode() {
  const [shouldFallback, setShouldFallback] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const evaluate = () => {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const saveData = !!(conn && conn.saveData);
      const effType = (conn && conn.effectiveType) || "";
      const slow = /(^|-)2g$/i.test(effType); // 2g ou slow-2g
      const reducedData = window.matchMedia && window.matchMedia("(prefers-reduced-data: reduce)").matches;
      return saveData || slow || reducedData;
    };

    setShouldFallback(evaluate());

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const onChange = () => setShouldFallback(evaluate());
    conn?.addEventListener?.("change", onChange);
    let mqReduced = null;
    try {
      mqReduced = window.matchMedia("(prefers-reduced-data: reduce)");
      mqReduced.addEventListener?.("change", onChange);
    } catch { /* ignore */ }
    return () => {
      conn?.removeEventListener?.("change", onChange);
      mqReduced?.removeEventListener?.("change", onChange);
    };
  }, []);

  return shouldFallback;
}


export function useGroupCompanies() {
  const [data, setData] = useState(cache["group-companies"] || []);
  const [loading, setLoading] = useState(!cache["group-companies"]);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchList("group-companies")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  return { data, loading, error };
}

export function useCertifications() {
  const [data, setData] = useState(cache.certifications || []);
  const [loading, setLoading] = useState(!cache.certifications);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchList("certifications")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  return { data, loading, error };
}

export function useOffices() {
  const [data, setData] = useState(cache.offices || []);
  const [loading, setLoading] = useState(!cache.offices);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchList("offices")
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  return { data, loading, error };
}
